// ---------------------------------------------------------------------------
// Single entry point for talking to the Django API.
//
// - prefixes REACT_APP_API_URL
// - adds the DRF `Authorization: Token ...` header when a token is given
// - JSON-encodes bodies and decodes responses
// - throws ApiError for any non-2xx response
// - tells the auth layer when a token has been rejected (401)
// ---------------------------------------------------------------------------

const API_URL = process.env.REACT_APP_API_URL;

export class ApiError extends Error {
  constructor(status, data) {
    super(messageFromData(data) ?? `Request failed (${status})`);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

// DRF and the family views return errors under a few different keys.
function messageFromData(data) {
  if (!data || typeof data !== "object") return null;
  return data.detail ?? data.error ?? null;
}

let onUnauthorized = null;

/** Register a callback run when an authenticated request gets a 401. */
export function setUnauthorizedHandler(handler) {
  onUnauthorized = handler;
}

export async function apiRequest(path, { method = "GET", token, body } = {}) {
  const headers = { Accept: "application/json" };
  if (token) headers.Authorization = `Token ${token}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  const text = await response.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  if (!response.ok) {
    if (response.status === 401 && token && onUnauthorized) onUnauthorized();
    throw new ApiError(response.status, data);
  }

  return data;
}

export const api = {
  get: (path, token) => apiRequest(path, { token }),
  post: (path, token, body) => apiRequest(path, { method: "POST", token, body }),
  patch: (path, token, body) =>
    apiRequest(path, { method: "PATCH", token, body }),
  delete: (path, token) => apiRequest(path, { method: "DELETE", token }),
};

/** A message suitable for showing the user. */
export function errorMessage(err, fallback = "Something went wrong.") {
  if (err instanceof ApiError) return err.message;
  if (err instanceof TypeError) return "Couldn't reach the server.";
  return err?.message || fallback;
}
